// src/pages/NomPeriodosPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import Toast, { useToast } from "../components/Toast";
import {
  obtenerPeriodos, crearPeriodo, actualizarPeriodo, eliminarPeriodo,
  generarNomina, calcularNomina,
} from "../services/nominaService";

const TIPO_LABEL = { S: "Semanal", C: "Catorcenal", Q: "Quincenal", M: "Mensual" };
const STATUS_BADGE = {
  A: { label: "Abierto",    bg: "#dbeafe", color: "#1d4ed8" },
  C: { label: "Calculado",  bg: "#dcfce7", color: "#16a34a" },
  P: { label: "Pagado",     bg: "#f1f5f9", color: "#475569" },
};

const EMPTY = { Nombre: "", TipoPeriodo: "Q", NumPeriodo: 1, Anio: new Date().getFullYear(), FechaInicio: "", FechaFin: "" };

const fmt = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "";
const money = (n) => Number(n ?? 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" });

export default function NomPeriodosPage() {
  const navigate = useNavigate();
  const { toasts, closeToast, success: ok, error: err } = useToast();

  const anioActual = new Date().getFullYear();
  const [anio, setAnio] = useState(anioActual);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey(k => k + 1);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [calculating, setCalculating] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    obtenerPeriodos()
      .then(data => { if (!cancelled) setAllRows(data ?? []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey]);

  const rows = allRows.filter(r => r.anio === anio);

  const openNew = () => { setEditId(null); setForm({ ...EMPTY, Anio: anio }); setShowModal(true); };
  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      Nombre: row.nombre, TipoPeriodo: row.tipoPeriodo, NumPeriodo: row.numPeriodo,
      Anio: row.anio,
      FechaInicio: row.fechaInicio ? row.fechaInicio.slice(0, 10) : "",
      FechaFin: row.fechaFin ? row.fechaFin.slice(0, 10) : "",
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const dto = { ...form, Anio: Number(form.Anio), NumPeriodo: Number(form.NumPeriodo) };
      if (editId) { await actualizarPeriodo(editId, dto); ok("Periodo actualizado"); }
      else { await crearPeriodo(dto); ok("Periodo creado"); }
      setShowModal(false);
      reload();
    } catch { err("Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este periodo?")) return;
    try { await eliminarPeriodo(id); ok("Eliminado"); reload(); }
    catch (e) { err(e?.response?.data || "No se pudo eliminar"); }
  };

  const handleGenerar = async (id) => {
    setGenerating(id);
    try {
      const res = await generarNomina(id);
      ok(`${res.creados} empleado(s) agregados al periodo`);
      reload();
    } catch (e) { err(e?.response?.data || "Error al generar"); }
    finally { setGenerating(null); }
  };

  const handleCalcular = async (id) => {
    if (!window.confirm("¿Calcular ISR e IMSS para todos los empleados del periodo?")) return;
    setCalculating(id);
    try {
      const res = await calcularNomina(id);
      ok(`Cálculo completado: ${res.calculadas} nómina(s)`);
      reload();
    } catch (e) { err(e?.response?.data || "Error al calcular"); }
    finally { setCalculating(null); }
  };

  const aniosEnDatos = [...new Set(allRows.map(r => r.anio))].sort((a, b) => b - a);
  const anios = [...new Set([anioActual + 1, anioActual, anioActual - 1, anioActual - 2, ...aniosEnDatos])].sort((a, b) => b - a);

  const columns = [
    { key: "nombre", title: "Nombre" },
    { key: "tipoPeriodo", title: "Tipo", render: r => TIPO_LABEL[r.tipoPeriodo] ?? r.tipoPeriodo, style: { width: 110 } },
    { key: "numPeriodo", title: "#", style: { width: 50, textAlign: "center" } },
    { key: "fechaInicio", title: "Inicio", render: r => fmt(r.fechaInicio?.slice(0, 10)), style: { width: 110 } },
    { key: "fechaFin", title: "Fin", render: r => fmt(r.fechaFin?.slice(0, 10)), style: { width: 110 } },
    { key: "totalEmpleados", title: "Empl.", style: { width: 60, textAlign: "center" } },
    { key: "totalNeto", title: "Total Neto", render: r => money(r.totalNeto), style: { width: 130, textAlign: "right" } },
    {
      key: "status", title: "Estatus",
      render: r => {
        const s = STATUS_BADGE[r.status] ?? STATUS_BADGE.A;
        return <span style={{ padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600, background: s.bg, color: s.color }}>{s.label}</span>;
      },
      style: { width: 100 },
    },
    {
      key: "_acc", title: "",
      render: r => (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => navigate(`/nomina/periodos/${r.id}/calculo`)}>Ver</button>
          {r.status === "A" && <>
            <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => openEdit(r)}>Editar</button>
            <button className="lmd-btn lmd-btn-xs lmd-btn-outline" onClick={() => handleGenerar(r.id)} disabled={generating === r.id}>
              {generating === r.id ? "..." : "Generar"}
            </button>
            {r.totalEmpleados > 0 && (
              <button className="lmd-btn lmd-btn-xs lmd-btn-primary" onClick={() => handleCalcular(r.id)} disabled={calculating === r.id}>
                {calculating === r.id ? "..." : "Calcular"}
              </button>
            )}
            <button className="lmd-btn lmd-btn-xs lmd-btn-danger-outline" onClick={() => handleDelete(r.id)}>Eliminar</button>
          </>}
        </div>
      ),
      style: { width: 240 },
    },
  ];

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader title="Periodos de Nómina" subtitle="Gestión de periodos de pago" />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: "var(--clr-text-muted)" }}>Año:</label>
          <select className="lmd-select" value={anio} onChange={e => setAnio(Number(e.target.value))} style={{ width: 100 }}>
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <button className="lmd-btn lmd-btn-primary" onClick={openNew}>+ Nuevo Periodo</button>
      </div>

      <DataTable columns={columns} rows={rows} keyField="id" loading={loading} />

      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 28, width: "95%", maxWidth: 480, position: "relative" }}>
            <button onClick={() => setShowModal(false)} style={{ position: "absolute", top: 12, right: 16, border: "none", background: "none", fontSize: 20, cursor: "pointer" }}>✕</button>
            <h3 style={{ margin: "0 0 20px", fontSize: 16 }}>{editId ? "Editar Periodo" : "Nuevo Periodo"}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Nombre *</label>
                <input className="lmd-input" value={form.Nombre} required style={{ width: "100%" }}
                  placeholder="Quincena 1 Enero 2025"
                  onChange={e => setForm(f => ({ ...f, Nombre: e.target.value }))} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Tipo *</label>
                  <select className="lmd-select" value={form.TipoPeriodo} required style={{ width: "100%" }}
                    onChange={e => setForm(f => ({ ...f, TipoPeriodo: e.target.value }))}>
                    <option value="S">Semanal</option>
                    <option value="C">Catorcenal</option>
                    <option value="Q">Quincenal</option>
                    <option value="M">Mensual</option>
                  </select>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>No. Periodo *</label>
                  <input className="lmd-input" type="number" min="1" value={form.NumPeriodo} required style={{ width: "100%" }}
                    onChange={e => setForm(f => ({ ...f, NumPeriodo: e.target.value }))} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Año *</label>
                  <input className="lmd-input" type="number" min="2020" max="2035" value={form.Anio} required style={{ width: "100%" }}
                    onChange={e => setForm(f => ({ ...f, Anio: e.target.value }))} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Fecha Inicio *</label>
                  <input className="lmd-input" type="date" value={form.FechaInicio} required style={{ width: "100%" }}
                    onChange={e => setForm(f => ({ ...f, FechaInicio: e.target.value }))} />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--clr-text-muted)", marginBottom: 4 }}>Fecha Fin *</label>
                  <input className="lmd-input" type="date" value={form.FechaFin} required style={{ width: "100%" }}
                    onChange={e => setForm(f => ({ ...f, FechaFin: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" className="lmd-btn lmd-btn-outline" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="lmd-btn lmd-btn-primary" disabled={saving}>{saving ? "Guardando..." : "Guardar"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
